import { Button, Form, Input, MenuProps } from 'antd';
import { get } from 'lodash';
import React, { memo, useEffect, useMemo } from 'react';
import { FormItem, INLTSwitch, SimpleSelect } from '@xbcb/form-item-components';
import { WorkOrderRecordType } from '@xbcb/shared-types';
import { getEnv } from '@xbcb/ui-utils';
import { useHtsValues } from 'context/htsValues';
import { isReadOnly, shouldRequire } from 'libs/readWriteControl';
import { useArrivalDate, useWorkOrderTaskConfiguration } from 'libs/hooks';
import extraPGAs from 'libs/extraPGAs';
import { isCpscFeatureEnabled } from 'featureFlags';
import { useTheme } from 'styled-components';
import { CommercialInvoiceBaseProps } from 'types';
import { StyledDiv, StyledDropdown } from './styles';
import { clearFields } from 'libs/clearFields';

const AD = 'ad';
const CV = 'cv';

export interface LineItemTariffPgaProps extends CommercialInvoiceBaseProps {
  index: number;
  setCvTabManuallyAdded: (show: boolean) => void;
  setAdTabManuallyAdded: (show: boolean) => void;
  adTabVisible: boolean;
  cvTabVisible: boolean;
}

/**
 * LineItemTariffPga
 *
 * PGA related fields for LineItem Tariff tabs.
 */

export const LineItemTariffPga: React.FC<LineItemTariffPgaProps> = ({
  fullNamePath,
  localNamePath,
  workOrderType,
  index,
  readOnly,
  disabled,
  required,
  setAdTabManuallyAdded,
  setCvTabManuallyAdded,
  adTabVisible,
  cvTabVisible,
}) => {
  const theme = useTheme();
  const { htsValues } = useHtsValues();
  const form = Form.useFormInstance();
  const { readWriteControlsConfig } = useWorkOrderTaskConfiguration();

  const fullSameAsCbpNamePath = useMemo(
    () => [...fullNamePath, 'pgaDescription', 'sameAsCbp'],
    [fullNamePath],
  );
  const sameAsCbpNamePath = [...localNamePath, 'pgaDescription', 'sameAsCbp'];
  const fullExtraPgaFlagsNamePath = [...fullNamePath, 'extraPgaFlags'];
  const extraPgaFlagsNamePath = [...localNamePath, 'extraPgaFlags'];
  const fullPgaFlagsNamePath = [...fullNamePath, 'pga'];

  const isType86 = workOrderType === WorkOrderRecordType.US_TYPE86_ENTRY;
  const isPsc =
    workOrderType === WorkOrderRecordType.US_POST_SUMMARY_CORRECTION;

  const htsObj = htsValues.htsObjects[index];

  useEffect(() => {
    const sameAsCbp = form.getFieldValue(fullSameAsCbpNamePath);
    // Default to same as CBP description
    if (sameAsCbp === undefined && !isPsc) {
      form.setFieldValue(fullSameAsCbpNamePath, true);
    }
  }, [form, fullSameAsCbpNamePath, isPsc]);

  const normalPGAFlags: string[] = get(htsObj, 'pga', []);
  const extraPGAFlags =
    form.getFieldValue([...fullNamePath, 'extraPgaFlags']) || [];
  const pgaFlags = [...normalPGAFlags, ...extraPGAFlags];

  const { stage } = getEnv();
  const shipperId = Form.useWatch(['group', 'shipper', 'id'], {
    form,
    preserve: true,
  });
  const arrivalDate = useArrivalDate();
  const cpscEnabled = isCpscFeatureEnabled({
    stage,
    time: arrivalDate,
    shipper: shipperId,
  });

  const extraPGAOptions = Object.entries(extraPGAs).reduce(
    (
      acc: { [key: string]: string },
      [extraFlag, { equivalentFlags, label }],
    ) => {
      // Filter out CP1 (CPSC) when the feature flag is disabled
      if (!cpscEnabled && extraFlag === 'CP1') return acc;
      if (
        !normalPGAFlags.includes(extraFlag) && // check that "main" flag isn't already present (the key in extraPGAs)
        !equivalentFlags.some(
          (equivalentFlag) => normalPGAFlags.includes(equivalentFlag), // check that "equivalent" flags aren't already present.
        )
      ) {
        acc[extraFlag] = label;
      }
      return acc;
    },
    {},
  );

  const caseNumberMenuItems = [];
  if (!adTabVisible)
    caseNumberMenuItems.push({ key: AD, label: 'Add Anti-Dumping Duty' });
  if (!cvTabVisible)
    caseNumberMenuItems.push({ key: CV, label: 'Add Countervailing Duty' });
  const caseNumberMenuProps: MenuProps = {
    items: caseNumberMenuItems,
    onClick: (e) => {
      if (e.key === AD) {
        setAdTabManuallyAdded(true);
      } else if (e.key === CV) {
        setCvTabManuallyAdded(true);
      }
    },
  };

  return (
    <>
      {!isPsc && pgaFlags.length > 0 && (
        <div>
          {!readOnly && (
            <StyledDiv>
              PGA description same as CBP?{' '}
              <INLTSwitch
                field={sameAsCbpNamePath}
                size="small"
                disabled={disabled}
                onWord=""
                offWord=""
                $inline
              />
            </StyledDiv>
          )}
          <Form.Item
            shouldUpdate={(prevValues, curValues) =>
              get(prevValues, fullSameAsCbpNamePath) !==
              get(curValues, fullSameAsCbpNamePath)
            }
            noStyle
          >
            {() =>
              !form.getFieldValue(fullSameAsCbpNamePath) ? (
                <FormItem
                  required={required}
                  label="PGA Description"
                  name={[...localNamePath, 'text']}
                  rules={[{ required, message: ' ' }]}
                  $itemSize={theme.size.short_medium}
                  $readOnly={readOnly}
                >
                  <Input.TextArea disabled={disabled} autoSize maxLength={50} />
                </FormItem>
              ) : (
                readOnly && <div>PGA description is same as CBP</div>
              )
            }
          </Form.Item>
        </div>
      )}
      <div>
        <SimpleSelect
          form={form}
          label="Additional Compliance Data"
          disabled={disabled}
          readOnly={
            readOnly ||
            isReadOnly(
              'invoices.products.lines.tariffs.pga',
              readWriteControlsConfig,
            )
          }
          dropdownMatchSelectWidth={false}
          multiple
          $itemSize={theme.size.short_medium}
          required={shouldRequire(
            'invoices.products.lines.tariffs.pga',
            readWriteControlsConfig,
            false,
          )}
          fullNamePath={fullExtraPgaFlagsNamePath}
          localNamePath={extraPgaFlagsNamePath}
          map={extraPGAOptions}
          maxTagCount={4}
          onDeselect={(value) => {
            if (value) {
              clearFields({
                form,
                fullNamePath: fullPgaFlagsNamePath,
                fields: [String(value)],
              })();
            }
          }}
        />
        {!readOnly &&
          !isReadOnly(
            'invoices.products.lines.tariffs.pga',
            readWriteControlsConfig,
          ) &&
          !disabled &&
          !isType86 &&
          !(adTabVisible && cvTabVisible) && (
            <StyledDropdown menu={caseNumberMenuProps}>
              <Button>Add Case Number</Button>
            </StyledDropdown>
          )}
      </div>
    </>
  );
};

export default memo(LineItemTariffPga);
